/**
 * The navigation model, ported from the mockup's data/nav.js.
 *
 * Two orthogonal axes:
 *   SCOPE — what am I looking at?  platform ⊃ group ⊃ org ⊃ agent
 *   TOPIC — what kind of page?     overview · posture · monitoring ·
 *                                  detection · admin
 *
 * The list is FLAT on purpose: `scope` and `topic` are fields, so every page
 * is written down exactly once. A nested tree gave pages two homes, and
 * duplication in a menu is a bug.
 *
 * Visibility is by PERMISSION, not role — so when an admin reconfigures a
 * role the menu follows automatically (handoff §3).
 */
import type { PermissionKey } from "@features/auth/permissions";
import type { ModuleName }    from "@features/auth/hooks/useModule";
import { IS_ONPREM, ONPREM_REMOVED_PAGES } from "@shared/config/edition";

export type ScopeId = "platform" | "group" | "org" | "agent";
export type TopicId = "overview" | "posture" | "monitoring" | "detection" | "admin";

export interface NavScope {
  id: ScopeId;
  label: string;
  icon: string;
  hue: string;
  blurb: string;
}

export interface NavItem {
  id: string;
  label: string;
  icon: string;
  /** Router path. */
  to: string;
  scope: ScopeId;
  topic: TopicId;
  /** Hidden unless the user holds this permission. */
  perm?: PermissionKey;
  /** Route exists but the API isn't ready yet (handoff §6). */
  soon?: boolean;
  /** Minimum scope_level to see this item (this scope or broader). */
  scopeLevel?: "platform" | "group" | "org";
  /** Only this exact scope_level sees it — broader scopes do NOT. */
  exactScope?: "platform" | "group" | "org";
  /** When true, this item is rendered in the LEFT SIDEBAR (not in the
   *  TopNav Browse panel). Still appears in the activeItem lookup for
   *  the TopNav crumb so the page title reads correctly, just not in
   *  the Browse sections panel. */
  sidebar?: boolean;
  /**
   * Hidden entirely when the org has this module disabled (handoff Variation
   * A). Platform/group admins are `unrestricted` and always see it. Distinct
   * from `perm`: module = "did the org buy this", perm = "may this user use it".
   */
  module?: ModuleName;
  /**
   * Extra paths that should light this item up, as a regex source string.
   * Needed when the nav links to a landing page but the real screen lives at
   * a parameterised URL — e.g. FIM's picker is /fim-monitoring while the
   * dashboard is /agents/{id}/fim. Kept as a string so NAV_ITEMS stays
   * plain serializable data.
   */
  altMatch?: string;
}

export const NAV_SCOPES: NavScope[] = [
  { id: "platform", label: "Platform",     icon: "fa-layer-group",     hue: "amber",  blurb: "Every tenant group" },
  { id: "group",    label: "Group",        icon: "fa-building-shield", hue: "purple", blurb: "This group and its orgs" },
  { id: "org",      label: "Organization", icon: "fa-city",            hue: "blue",   blurb: "One customer org" },
  { id: "agent",    label: "Agent",        icon: "fa-server",          hue: "cyan",   blurb: "A single monitored host" },
];

export const NAV_TOPICS: Array<{ id: TopicId; label: string; icon: string }> = [
  { id: "overview",   label: "Overview",             icon: "fa-layer-group" },
  { id: "posture",    label: "Security Posture",     icon: "fa-shield-halved" },
  { id: "monitoring", label: "Monitoring",           icon: "fa-gauge-high" },
  { id: "detection",  label: "Detection & Response", icon: "fa-crosshairs" },
  { id: "admin",      label: "Administration",       icon: "fa-gear" },
];

/**
 * Every page, exactly once.
 * `soon: true` marks screens whose telemetry endpoints aren't built yet.
 */
const ALL_NAV_ITEMS: NavItem[] = [
  /* ---- platform ---- */
  { id: "groups", label: "Group Management", icon: "fa-sitemap", to: "/groups",
    scope: "platform", topic: "overview", perm: "group:read", scopeLevel: "platform" },

  /* ---- tenant group ---- */
  { id: "organizations", label: "Organizations", icon: "fa-city", to: "/organizations",
    scope: "group", topic: "overview", perm: "org:read", scopeLevel: "group", sidebar: true },
  { id: "group-dashboard", label: "Group Dashboard", icon: "fa-chart-line", to: "/group-dashboard",
    scope: "group", topic: "overview", perm: "org:read", scopeLevel: "group", sidebar: true },

  /* ---- organization: overview + admin (org admin) ---- */
  { id: "org-dashboard", label: "Dashboard", icon: "fa-gauge-high", to: "/org-dashboard",
    scope: "org", topic: "admin", exactScope: "org" },
  { id: "user-management", label: "User Management", icon: "fa-users", to: "/user-management",
    scope: "org", topic: "admin", perm: "user:read", exactScope: "org" },
  { id: "agent-enrollment", label: "Agent Enrollment", icon: "fa-download", to: "/agent-enrollment",
    scope: "org", topic: "admin", perm: "agent:enroll", exactScope: "org" },
  { id: "fim-management", label: "FIM Directories", icon: "fa-folder-tree", to: "/fim-management",
    scope: "org", topic: "admin", perm: "agent:read", exactScope: "org", module: "fim" },
  { id: "alert-thresholds", label: "Alert Thresholds", icon: "fa-sliders", to: "/alert-thresholds",
    scope: "org", topic: "admin", perm: "alerts:read", exactScope: "org" },
  /* Event Volume — admin-only page that lives in the sidebar's Admin
     group. Registered here (even though not shown in the TopNav) so the
     Header's title resolver (AppShell → itemFor(pathname)?.label) can
     find a label for /event-volume. Without this entry the title falls
     through to SHELL.defaultTitle ("Platform Console"). All other
     sidebar-only pages (user-management, agent-enrollment, fim-management,
     alert-thresholds, endpoint-inventory) have the same dual registration
     for this reason. Permission matches /event-volume's route guard. */
  { id: "event-volume", label: "Event Volume", icon: "fa-chart-area", to: "/event-volume",
    scope: "org", topic: "admin", perm: "events:read", exactScope: "org" },

  /* ---- organization: posture / monitoring / detection (org admin + viewer) ----
     These are the analyst-facing screens. They gate on read permissions the
     viewer role also holds, and several gate on a module so a disabled feature
     vanishes entirely (handoff Variation A). */
  { id: "endpoint-inventory", label: "Endpoint Inventory", icon: "fa-server",
    to: "/endpoint-inventory", scope: "org", topic: "admin", perm: "agent:read" },
  { id: "agent-dashboard", label: "Agent Dashboard", icon: "fa-microchip",
    to: "/agent-dashboard", scope: "org", topic: "overview", perm: "agent:read" },
  { id: "resource-monitoring", label: "Resource Monitoring", icon: "fa-gauge-high",
    to: "/resource-monitoring", scope: "org", topic: "admin", perm: "agent:read", module: "sysmon" },
  { id: "system-monitoring", label: "System Monitoring", icon: "fa-desktop",
    to: "/system-monitoring", scope: "org", topic: "monitoring", perm: "agent:read", module: "sysmon" },
  { id: "cis-benchmark", label: "CIS Benchmark", icon: "fa-clipboard-check",
    to: "/cis-benchmark", scope: "org", topic: "posture", perm: "agent:read", module: "cis" },
  { id: "compliance", label: "Security Compliance", icon: "fa-file-shield",
    to: "/compliance", scope: "org", topic: "posture", perm: "agent:read", module: "compliance" },
  { id: "vulnerability-detection", label: "Vulnerability Detection", icon: "fa-bug",
    to: "/vulnerability-detection", scope: "org", topic: "posture", perm: "agent:read", module: "vuln" },
  { id: "mitre-attack", label: "MITRE ATT&CK", icon: "fa-diagram-project",
    to: "/mitre-attack", scope: "org", topic: "admin", perm: "agent:read", module: "mitre" },
  { id: "authentication-access", label: "Authentication & Access", icon: "fa-key",
    to: "/authentication-access", scope: "org", topic: "detection", perm: "agent:read" },
  { id: "threat-hunting", label: "Threat Hunting", icon: "fa-crosshairs",
    to: "/threat-hunting", scope: "org", topic: "detection", perm: "agent:read", module: "threat" },

  /* ---- agent scope ---- */
  /* The dashboard itself lives at /agents/:agentId/fim; this entry points at
     the agent picker that leads there. Open to every signed-in user — no
     `perm`, no scope restriction — since the server authorises per agent. */

{ id: "fim-monitoring", label: "File Integrity Monitoring", icon: "fa-file-shield",
  to: "/file-integrity-monitoring", scope: "agent", topic: "monitoring",
  altMatch: "^/fim-monitoring$", module: "fim" },
  /* ---- platform settings ---- */ 
  { id: "settings", label: "Settings", icon: "fa-gear", to: "/settings",
    scope: "platform", topic: "admin", perm: "config:manage" },
];

/** Pages of this build's edition (on-prem drops multi-org and placeholder pages). */
export const NAV_ITEMS: NavItem[] = IS_ONPREM
  ? [
      ...ALL_NAV_ITEMS.filter((i) => !ONPREM_REMOVED_PAGES.has(i.id)),
      // On-prem only: licence details (organisation, limits, dates, last check-in).
      { id: "licence", label: "Licence", icon: "fa-key", to: "/licence",
        scope: "org", topic: "admin", exactScope: "org" },
    ]
  : ALL_NAV_ITEMS;


// /**
//  * The navigation model, ported from the mockup's data/nav.js.
//  *
//  * Two orthogonal axes:
//  *   SCOPE — what am I looking at?  platform ⊃ group ⊃ org ⊃ agent
//  *   TOPIC — what kind of page?     overview · posture · monitoring ·
//  *                                  detection · admin
//  *
//  * The list is FLAT on purpose: `scope` and `topic` are fields, so every page
//  * is written down exactly once. A nested tree gave pages two homes, and
//  * duplication in a menu is a bug.
//  *
//  * Visibility is by PERMISSION, not role — so when an admin reconfigures a
//  * role the menu follows automatically (handoff §3).
//  */
// import type { PermissionKey } from "@features/auth/permissions";
// import type { ModuleName }    from "@features/auth/hooks/useModule";

// export type ScopeId = "platform" | "group" | "org" | "agent";
// export type TopicId = "overview" | "posture" | "monitoring" | "detection" | "admin";

// export interface NavScope {
//   id: ScopeId;
//   label: string;
//   icon: string;
//   hue: string;
//   blurb: string;
// }

// export interface NavItem {
//   id: string;
//   label: string;
//   icon: string;
//   /** Router path. */
//   to: string;
//   scope: ScopeId;
//   topic: TopicId;
//   /** Hidden unless the user holds this permission. */
//   perm?: PermissionKey;
//   /** Route exists but the API isn't ready yet (handoff §6). */
//   soon?: boolean;
//   /** Minimum scope_level to see this item (this scope or broader). */
//   scopeLevel?: "platform" | "group" | "org";
//   /** Only this exact scope_level sees it — broader scopes do NOT. */
//   exactScope?: "platform" | "group" | "org";
//   /**
//    * Hidden entirely when the org has this module disabled (handoff Variation
//    * A). Platform/group admins are `unrestricted` and always see it. Distinct
//    * from `perm`: module = "did the org buy this", perm = "may this user use it".
//    */
//   module?: ModuleName;
//   /**
//    * Extra paths that should light this item up, as a regex source string.
//    * Needed when the nav links to a landing page but the real screen lives at
//    * a parameterised URL — e.g. FIM's picker is /fim-monitoring while the
//    * dashboard is /agents/{id}/fim. Kept as a string so NAV_ITEMS stays
//    * plain serializable data.
//    */
//   altMatch?: string;
// }

// export const NAV_SCOPES: NavScope[] = [
//   { id: "platform", label: "Platform",     icon: "fa-layer-group",     hue: "amber",  blurb: "Every tenant group" },
//   { id: "group",    label: "Group",        icon: "fa-building-shield", hue: "purple", blurb: "This group and its orgs" },
//   { id: "org",      label: "Organization", icon: "fa-city",            hue: "blue",   blurb: "One customer org" },
//   { id: "agent",    label: "Agent",        icon: "fa-server",          hue: "cyan",   blurb: "A single monitored host" },
// ];

// export const NAV_TOPICS: Array<{ id: TopicId; label: string; icon: string }> = [
//   { id: "overview",   label: "Overview",             icon: "fa-layer-group" },
//   { id: "posture",    label: "Security Posture",     icon: "fa-shield-halved" },
//   { id: "monitoring", label: "Monitoring",           icon: "fa-gauge-high" },
//   { id: "detection",  label: "Detection & Response", icon: "fa-crosshairs" },
//   { id: "admin",      label: "Administration",       icon: "fa-gear" },
// ];

// /**
//  * Every page, exactly once.
//  * `soon: true` marks screens whose telemetry endpoints aren't built yet.
//  */
// export const NAV_ITEMS: NavItem[] = [
//   /* ---- platform ---- */
//   { id: "groups", label: "Group Management", icon: "fa-sitemap", to: "/groups",
//     scope: "platform", topic: "overview", perm: "group:read", scopeLevel: "platform" },

//   /* ---- tenant group ---- */
//   { id: "organizations", label: "Organizations", icon: "fa-city", to: "/organizations",
//     scope: "group", topic: "overview", perm: "org:read", scopeLevel: "group" },
//   { id: "group-dashboard", label: "Group Dashboard", icon: "fa-chart-line", to: "/group-dashboard",
//     scope: "group", topic: "overview", perm: "org:read", scopeLevel: "group" },

//   /* ---- organization: overview + admin (org admin) ---- */
//   { id: "org-dashboard", label: "Dashboard", icon: "fa-gauge-high", to: "/org-dashboard",
//     scope: "org", topic: "admin", exactScope: "org" },
//   { id: "user-management", label: "User Management", icon: "fa-users", to: "/user-management",
//     scope: "org", topic: "admin", perm: "user:read", exactScope: "org" },
//   { id: "agent-enrollment", label: "Agent Enrollment", icon: "fa-download", to: "/agent-enrollment",
//     scope: "org", topic: "admin", perm: "agent:enroll", exactScope: "org" },
//   { id: "fim-management", label: "FIM Directories", icon: "fa-folder-tree", to: "/fim-management",
//     scope: "org", topic: "admin", perm: "agent:read", exactScope: "org", module: "fim" },
//   { id: "alert-thresholds", label: "Alert Thresholds", icon: "fa-sliders", to: "/alert-thresholds",
//     scope: "org", topic: "admin", perm: "alerts:read", exactScope: "org" },

//   /* ---- organization: posture / monitoring / detection (org admin + viewer) ----
//      These are the analyst-facing screens. They gate on read permissions the
//      viewer role also holds, and several gate on a module so a disabled feature
//      vanishes entirely (handoff Variation A). */
//   { id: "endpoint-inventory", label: "Security Client Registry", icon: "fa-server",
//     to: "/endpoint-inventory", scope: "org", topic: "admin", perm: "agent:read" },
//   { id: "agent-dashboard", label: "Agent Dashboard", icon: "fa-microchip",
//     to: "/agent-dashboard", scope: "org", topic: "overview", perm: "agent:read" },
//   { id: "resource-monitoring", label: "Resource Monitoring", icon: "fa-gauge-high",
//     to: "/resource-monitoring", scope: "org", topic: "admin", perm: "agent:read", module: "sysmon" },
//   { id: "system-monitoring", label: "System Monitoring", icon: "fa-desktop",
//     to: "/system-monitoring", scope: "org", topic: "monitoring", perm: "agent:read", module: "sysmon" },
//   { id: "cis-benchmark", label: "CIS Benchmark", icon: "fa-clipboard-check",
//     to: "/cis-benchmark", scope: "org", topic: "posture", perm: "agent:read", module: "cis" },
//   { id: "compliance", label: "Security Compliance", icon: "fa-file-shield",
//     to: "/compliance", scope: "org", topic: "posture", perm: "agent:read", module: "compliance" },
//   { id: "vulnerabilities", label: "Vulnerabilities", icon: "fa-bug",
//     to: "/vulnerabilities", scope: "org", topic: "posture", perm: "agent:read", module: "vuln" },
//   { id: "mitre-attack", label: "MITRE ATT&CK", icon: "fa-diagram-project",
//     to: "/mitre-attack", scope: "org", topic: "admin", perm: "agent:read", module: "mitre" },
//   { id: "authentication-access", label: "Authentication & Access", icon: "fa-key",
//     to: "/authentication-access", scope: "org", topic: "detection", perm: "agent:read" },
//   { id: "threat-hunting", label: "Threat Hunting", icon: "fa-crosshairs",
//     to: "/threat-hunting", scope: "org", topic: "detection", perm: "agent:read", module: "threat" },

//   /* ---- agent scope ---- */
//   /* The dashboard itself lives at /agents/:agentId/fim; this entry points at
//      the agent picker that leads there. Open to every signed-in user — no
//      `perm`, no scope restriction — since the server authorises per agent. */

// { id: "fim-monitoring", label: "File Integrity Monitoring", icon: "fa-file-shield",
//   to: "/fim", scope: "agent", topic: "monitoring",
//   altMatch: "^/fim-monitoring$", module: "fim" },
//   /* ---- platform settings ---- */ 
//   { id: "settings", label: "Settings", icon: "fa-gear", to: "/settings",
//     scope: "platform", topic: "admin", perm: "config:manage" },
// ];
